import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Users, ChartLine, CurrencyDollar, GraduationCap, BookOpen, ArrowsClockwise, Crown, Lightning, CheckCircle, WarningCircle, Printer, FileText, Trash, ChatCircleDots, UploadSimple, DownloadSimple } from "@phosphor-icons/react";
import BulkUploader from "@/components/BulkUploader";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Cell,
} from "recharts";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";

export default function AdminPage() {
    const { user, ready } = useAuth();
    const nav = useNavigate();
    const [overview, setOverview] = useState(null);
    const [users, setUsers] = useState([]);
    const [results, setResults] = useState([]);
    const [courses, setCourses] = useState([]);
    const [regenCourse, setRegenCourse] = useState("");
    const [regenTopic, setRegenTopic] = useState("");
    const [regenLoading, setRegenLoading] = useState(false);
    const [prewarmJob, setPrewarmJob] = useState(null);
    const [prewarmForce, setPrewarmForce] = useState(false);
    const [prewarmStarting, setPrewarmStarting] = useState(false);
    const [prewarmScope, setPrewarmScope] = useState({}); // { courseId: true }
    // Resources tab state
    const [subjects, setSubjects] = useState([]);
    const [resources, setResources] = useState([]);
    const [uploadForm, setUploadForm] = useState({
        subject_code: "", paper_type: "past_paper", title: "", year: "", session: "", paper_number: "", notes: "",
    });
    const [uploadFile, setUploadFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    // Enquiries tab state
    const [enquiries, setEnquiries] = useState([]);

    useEffect(() => {
        if (!ready) return;
        if (!user || !user.is_admin) { nav("/"); return; }
        Promise.all([
            api.get("/admin/overview").then(r => r.data),
            api.get("/admin/users").then(r => r.data),
            api.get("/admin/results").then(r => r.data),
            api.get("/courses").then(r => r.data),
            api.get("/lessons/prewarm/latest").then(r => r.data).catch(() => null),
            api.get("/resources/subjects").then(r => r.data).catch(() => []),
            api.get("/admin/resources").then(r => r.data).catch(() => []),
            api.get("/admin/enquiries").then(r => r.data).catch(() => []),
        ]).then(([o, u, r, c, p, s, rs, en]) => {
            setOverview(o); setUsers(u); setResults(r); setCourses(c); setPrewarmJob(p);
            setSubjects(s); setResources(rs); setEnquiries(en);
          })
          .catch(() => toast.error("Failed to load admin data"));
    }, [user, ready, nav]);

    // Poll running pre-warm job every 3s
    useEffect(() => {
        if (!prewarmJob || prewarmJob.status !== "running") return;
        const t = setInterval(async () => {
            try {
                const { data } = await api.get(`/lessons/prewarm/status/${prewarmJob.id}`);
                setPrewarmJob(data);
                if (data.status !== "running") {
                    clearInterval(t);
                    // Refresh overview so the "lessons cached" KPI catches up
                    api.get("/admin/overview").then(r => setOverview(r.data)).catch(() => {});
                    toast.success(`Pre-warm complete: ${data.generated} generated, ${data.skipped} skipped, ${data.failed} failed`);
                }
            } catch { /* ignore */ }
        }, 3000);
        return () => clearInterval(t);
    }, [prewarmJob]);

    const startPrewarm = async () => {
        setPrewarmStarting(true);
        try {
            const selected = Object.keys(prewarmScope).filter(k => prewarmScope[k]);
            const payload = { force: prewarmForce };
            if (selected.length > 0) payload.course_ids = selected;
            const { data } = await api.post("/lessons/prewarm", payload);
            setPrewarmJob(data);
            toast.success(`Pre-warm queued: ${data.total} lessons across ${selected.length || "all"} course${selected.length === 1 ? "" : "s"}`);
        } catch (e) {
            if (e?.response?.status === 409) toast.error("A pre-warm job is already running");
            else toast.error(e?.response?.data?.detail || "Could not start pre-warm");
        } finally { setPrewarmStarting(false); }
    };

    const toggleScope = (cid) => setPrewarmScope(prev => ({ ...prev, [cid]: !prev[cid] }));
    const selectAllScope = () => setPrewarmScope(Object.fromEntries(courses.map(c => [c.id, true])));
    const clearScope = () => setPrewarmScope({});

    const cancelPrewarm = async () => {
        if (!prewarmJob) return;
        try {
            await api.post(`/lessons/prewarm/cancel/${prewarmJob.id}`);
            toast.success("Cancel signal sent — worker will stop after current lesson");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not cancel");
        }
    };

    const regenerate = async () => {
        if (!regenCourse || !regenTopic) return toast.error("Pick course and topic");
        setRegenLoading(true);
        try {
            await api.post("/lessons/regenerate", { course_id: regenCourse, topic: regenTopic });
            toast.success(`Regenerated lesson: ${regenTopic}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Regeneration failed");
        } finally { setRegenLoading(false); }
    };

    // ---- Resources ----
    const uploadResource = async (e) => {
        e.preventDefault();
        if (!uploadFile) return toast.error("Choose a PDF or DOC file first");
        if (!uploadForm.subject_code || !uploadForm.title) return toast.error("Subject and title are required");
        setUploading(true);
        const fd = new FormData();
        fd.append("file", uploadFile);
        Object.entries(uploadForm).forEach(([k, v]) => v && fd.append(k, v));
        try {
            const { data } = await api.post("/admin/resources/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setResources((prev) => [data, ...prev]);
            setUploadFile(null);
            setUploadForm({ subject_code: "", paper_type: "past_paper", title: "", year: "", session: "", paper_number: "", notes: "" });
            toast.success(`Uploaded: ${data.title}`);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Upload failed");
        } finally { setUploading(false); }
    };

    const deleteResource = async (id) => {
        if (!window.confirm("Delete this resource? Students will no longer see it.")) return;
        try {
            await api.delete(`/admin/resources/${id}`);
            setResources((prev) => prev.filter(r => r.id !== id));
            toast.success("Resource deleted");
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    // ---- Enquiries ----
    const updateEnquiryStatus = async (id, status) => {
        try {
            await api.patch(`/admin/enquiries/${id}`, { status });
            setEnquiries((prev) => prev.map(e => e.id === id ? { ...e, status } : e));
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not update");
        }
    };

    if (!ready || !overview) {
        return <div className="min-h-screen grid place-items-center"><div className="dot-pulse"></div></div>;
    }

    const selectedCourse = courses.find(c => c.id === regenCourse);

    return (
        <div className="min-h-screen" data-testid="admin-page">
            <Navbar />
            <section className="mx-auto max-w-7xl px-6 py-12">
                <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-3">
                    <Crown size={14} weight="fill"/> Wibpro Admin Console
                </div>
                <h1 className="font-serif text-5xl md:text-6xl text-ink leading-none">Cohort overview</h1>

                {/* KPIs */}
                <div className="grid md:grid-cols-4 gap-4 mt-10">
                    <Kpi icon={<Users size={18} weight="duotone"/>} label="Total students" value={overview.total_users} testid="kpi-users"/>
                    <Kpi icon={<Crown size={18} weight="fill" className="text-gold"/>} label="Premium" value={overview.premium_users} testid="kpi-premium"/>
                    <Kpi icon={<ChartLine size={18} weight="duotone"/>} label="Tests submitted" value={overview.total_tests_submitted} testid="kpi-tests"/>
                    <Kpi icon={<CurrencyDollar size={18} weight="duotone"/>} label="Revenue (USD)" value={`$${overview.revenue_usd}`} testid="kpi-revenue"/>
                </div>
                <div className="grid md:grid-cols-3 gap-4 mt-4">
                    <Kpi icon={<BookOpen size={18} weight="duotone"/>} label="Lessons cached" value={overview.lessons_generated} testid="kpi-lessons"/>
                    <Kpi icon={<Printer size={18} weight="duotone"/>} label="Lesson downloads" value={overview.lesson_downloads || 0} testid="kpi-downloads"/>
                    <Kpi icon={<GraduationCap size={18} weight="duotone"/>} label="Courses" value={courses.length} testid="kpi-courses"/>
                </div>

                <Tabs defaultValue="courses" className="mt-10">
                    <TabsList className="bg-parchment rounded-sm">
                        <TabsTrigger value="courses" data-testid="tab-courses">Course activity</TabsTrigger>
                        <TabsTrigger value="signups" data-testid="tab-signups">Signups</TabsTrigger>
                        <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
                        <TabsTrigger value="results" data-testid="tab-results">Recent results</TabsTrigger>
                        <TabsTrigger value="lessons" data-testid="tab-lessons">Lessons</TabsTrigger>
                        <TabsTrigger value="resources" data-testid="tab-resources">Papers</TabsTrigger>
                        <TabsTrigger value="enquiries" data-testid="tab-enquiries">
                            Enquiries {enquiries.filter(e => e.status === "new").length > 0 && (
                                <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded-sm bg-gold text-ink font-mono">
                                    {enquiries.filter(e => e.status === "new").length}
                                </span>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="courses" className="mt-6">
                        <div className="border border-ink/15 rounded-sm bg-bone p-6" data-testid="course-activity-panel">
                            <div className="text-xs uppercase tracking-[0.3em] text-ink/50 mb-4">Attempts per course</div>
                            <div className="h-96">
                                <ResponsiveContainer>
                                    <BarChart data={overview.course_stats} margin={{ left: -10, right: 10, top: 10, bottom: 60 }}>
                                        <CartesianGrid stroke="#0A254011" vertical={false}/>
                                        <XAxis dataKey="course_name" angle={-35} textAnchor="end" interval={0} tick={{ fontSize: 11, fill: "#0A2540" }} height={80}/>
                                        <YAxis tick={{ fontSize: 11, fill: "#0A2540" }}/>
                                        <Tooltip contentStyle={{ fontSize: 12, background: "#F6F5F2", border: "1px solid #0A2540" }}
                                            formatter={(v, k) => [v, k === "attempts" ? "Attempts" : "Avg %"]}/>
                                        <Bar dataKey="attempts" radius={[3,3,0,0]}>
                                            {overview.course_stats.map((_, i) => (
                                                <Cell key={i} fill={i % 2 === 0 ? "#0A2540" : "#C5A059"}/>
                                            ))}
                                        </Bar>
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                            <div className="mt-4 overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Course</TableHead>
                                            <TableHead>Category</TableHead>
                                            <TableHead className="text-right">Attempts</TableHead>
                                            <TableHead className="text-right">Avg %</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {overview.course_stats.length === 0 && (
                                            <TableRow><TableCell colSpan={4} className="text-center text-ink/50 py-8">No attempts yet.</TableCell></TableRow>
                                        )}
                                        {overview.course_stats.map(c => (
                                            <TableRow key={c.course_id} data-testid={`course-row-${c.course_id}`}>
                                                <TableCell className="font-serif text-ink">{c.course_name}</TableCell>
                                                <TableCell className="text-ink/60">{c.category}</TableCell>
                                                <TableCell className="text-right font-mono">{c.attempts}</TableCell>
                                                <TableCell className="text-right font-mono">{c.avg_score}%</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="signups" className="mt-6">
                        <div className="border border-ink/15 rounded-sm bg-bone p-6" data-testid="signups-panel">
                            <div className="text-xs uppercase tracking-[0.3em] text-ink/50 mb-4">New signups · last 14 days</div>
                            <div className="h-80">
                                <ResponsiveContainer>
                                    <LineChart data={overview.signups_by_day}>
                                        <CartesianGrid stroke="#0A254011"/>
                                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#0A2540" }}/>
                                        <YAxis tick={{ fontSize: 10, fill: "#0A2540" }} allowDecimals={false}/>
                                        <Tooltip contentStyle={{ fontSize: 12, background: "#F6F5F2", border: "1px solid #0A2540" }}/>
                                        <Line type="monotone" dataKey="count" stroke="#0A2540" strokeWidth={2} dot={{ fill: "#C5A059", r: 4 }}/>
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                            {overview.signups_by_day.length === 0 && (
                                <div className="text-center text-ink/50 py-4">No signups in the last 14 days.</div>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="users" className="mt-6">
                        <div className="border border-ink/15 rounded-sm bg-bone overflow-x-auto" data-testid="users-panel">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Name</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Joined</TableHead>
                                        <TableHead className="text-right">Tests</TableHead>
                                        <TableHead className="text-right">Plan</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {users.map(u => (
                                        <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                                            <TableCell className="font-serif text-ink">{u.name}{u.is_admin && <Crown size={12} className="ml-1 inline text-gold"/>}</TableCell>
                                            <TableCell className="text-ink/70">{u.email}</TableCell>
                                            <TableCell className="text-ink/60 font-mono text-xs">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                                            <TableCell className="text-right font-mono">{u.tests_taken}</TableCell>
                                            <TableCell className="text-right">
                                                <span className={`text-[10px] px-2 py-0.5 rounded-sm ${u.is_premium ? "bg-gold text-ink" : "bg-parchment text-ink/60"}`}>
                                                    {u.is_premium ? "Premium" : "Free"}
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="results" className="mt-6">
                        <div className="border border-ink/15 rounded-sm bg-bone overflow-x-auto" data-testid="results-panel">
                            <Table>
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Student</TableHead>
                                        <TableHead>Course</TableHead>
                                        <TableHead>Type</TableHead>
                                        <TableHead>Date</TableHead>
                                        <TableHead className="text-right">Score</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {results.map(r => (
                                        <TableRow key={r.id} data-testid={`result-row-${r.id}`}>
                                            <TableCell>
                                                <div className="font-serif text-ink">{r.user_name}</div>
                                                <div className="text-xs text-ink/50">{r.user_email}</div>
                                            </TableCell>
                                            <TableCell className="text-ink/70">{r.course_name}</TableCell>
                                            <TableCell><span className="text-[10px] uppercase tracking-widest text-ink/60">{r.test_type}</span></TableCell>
                                            <TableCell className="text-ink/60 font-mono text-xs">{new Date(r.created_at).toLocaleString()}</TableCell>
                                            <TableCell className="text-right font-mono">
                                                <span className={r.percent >= 75 ? "text-emerald-800" : r.percent >= 50 ? "text-gold-dark" : "text-oxblood"}>
                                                    {r.score}/{r.total} · {r.percent}%
                                                </span>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </TabsContent>

                    <TabsContent value="lessons" className="mt-6 space-y-6">
                        {/* Pre-warm cache */}
                        <div className="border border-gold/50 rounded-sm bg-bone p-6" data-testid="prewarm-panel">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-2">
                                <Lightning size={14} weight="fill"/> Pre-warm cache
                            </div>
                            <div className="font-serif text-2xl text-ink">Bulk-generate every lesson</div>
                            <div className="text-sm text-ink/60 mt-1 max-w-2xl">
                                Kicks off a background job that walks all {courses.length} courses × their topics and
                                fills the lesson cache with Claude Sonnet 4.6 content. Once complete, every student&apos;s
                                first visit to a lesson loads instantly.
                            </div>

                            <div className="mt-6 flex flex-wrap items-center gap-4">
                                <label className="flex items-center gap-2 text-sm text-ink/80">
                                    <input type="checkbox" checked={prewarmForce} onChange={e => setPrewarmForce(e.target.checked)} data-testid="prewarm-force-check" className="accent-ink"/>
                                    Force regenerate even cached lessons
                                </label>
                                <Button onClick={startPrewarm} disabled={prewarmStarting || prewarmJob?.status === "running"} className="bg-ink text-bone hover:bg-ink/90 rounded-sm" data-testid="btn-start-prewarm">
                                    <Lightning size={16} className="mr-2" weight="fill"/>
                                    {prewarmStarting ? "Starting…" : prewarmJob?.status === "running" ? "Job in progress…" : "Start pre-warm"}
                                </Button>
                                {prewarmJob?.status === "running" && (
                                    <Button onClick={cancelPrewarm} variant="outline" className="rounded-sm border-oxblood/60 text-oxblood hover:bg-oxblood hover:text-bone" data-testid="btn-cancel-prewarm">
                                        Cancel
                                    </Button>
                                )}
                            </div>

                            {/* Scope selector */}
                            <div className="mt-6 border-t border-ink/10 pt-6">
                                <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-ink/60">Scope</div>
                                        <div className="text-sm text-ink/70">Leave all unchecked to warm every course. Estimated cost per uncached lesson: <span className="font-mono">~$0.02</span></div>
                                    </div>
                                    <div className="flex gap-2 text-xs">
                                        <button type="button" onClick={selectAllScope} className="underline underline-offset-4 decoration-gold text-ink" data-testid="btn-scope-all">Select all</button>
                                        <span className="text-ink/30">·</span>
                                        <button type="button" onClick={clearScope} className="underline underline-offset-4 decoration-gold text-ink" data-testid="btn-scope-clear">Clear</button>
                                    </div>
                                </div>
                                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2" data-testid="prewarm-scope-grid">
                                    {courses.map(c => {
                                        const active = !!prewarmScope[c.id];
                                        const n_ch = (c.subjects || []).reduce((n, s) => n + s.chapters.length, 0);
                                        return (
                                            <button key={c.id} type="button" onClick={() => toggleScope(c.id)} data-testid={`prewarm-scope-${c.id}`}
                                                className={`flex items-center justify-between px-3 py-2 border rounded-sm text-left transition-colors ${
                                                    active ? "border-ink bg-ink text-bone" : "border-ink/20 bg-bone text-ink hover:border-ink/50"
                                                }`}>
                                                <div>
                                                    <div className="font-serif text-sm leading-tight">{c.name}</div>
                                                    <div className={`text-[10px] uppercase tracking-widest ${active ? "text-bone/60" : "text-ink/50"}`}>{n_ch} chapters</div>
                                                </div>
                                                <input type="checkbox" checked={active} readOnly className="accent-gold"/>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {prewarmJob && (
                                <div className="mt-6 border border-ink/15 rounded-sm p-5 bg-parchment/40" data-testid="prewarm-status">
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-ink/60">
                                            {prewarmJob.status === "running" && <><div className="dot-pulse"></div> Running</>}
                                            {prewarmJob.status === "completed" && <><CheckCircle size={14} weight="fill" className="text-emerald-700"/> Completed</>}
                                        </div>
                                        <div className="text-xs text-ink/50 font-mono">Job {prewarmJob.id?.slice(0, 8)}</div>
                                    </div>
                                    {/* progress bar */}
                                    <div className="h-2 bg-ink/10 rounded-sm overflow-hidden">
                                        <div className="h-full bg-gold transition-all duration-500"
                                             style={{ width: `${prewarmJob.total ? (prewarmJob.done * 100 / prewarmJob.total) : 0}%` }}></div>
                                    </div>
                                    <div className="mt-3 grid sm:grid-cols-4 gap-3 text-sm">
                                        <div><span className="text-ink/50 text-xs uppercase tracking-widest block">Progress</span><span className="font-mono text-ink">{prewarmJob.done} / {prewarmJob.total}</span></div>
                                        <div><span className="text-ink/50 text-xs uppercase tracking-widest block">Generated</span><span className="font-mono text-emerald-800">{prewarmJob.generated}</span></div>
                                        <div><span className="text-ink/50 text-xs uppercase tracking-widest block">Skipped</span><span className="font-mono text-ink/60">{prewarmJob.skipped}</span></div>
                                        <div><span className="text-ink/50 text-xs uppercase tracking-widest block">Failed</span><span className={`font-mono ${prewarmJob.failed ? "text-oxblood" : "text-ink/40"}`}>{prewarmJob.failed}</span></div>
                                    </div>
                                    {prewarmJob.last_target && prewarmJob.status === "running" && (
                                        <div className="mt-3 text-xs text-ink/60 italic">Just finished: {prewarmJob.last_target}</div>
                                    )}
                                    {prewarmJob.errors?.length > 0 && (
                                        <details className="mt-3 text-xs text-oxblood">
                                            <summary className="cursor-pointer flex items-center gap-1"><WarningCircle size={12}/> {prewarmJob.errors.length} error(s)</summary>
                                            <ul className="mt-2 space-y-1 pl-4">
                                                {prewarmJob.errors.map((e) => (
                                                    <li key={`${e.course_id}-${e.topic}`}>• {e.course_id} / {e.topic}: {e.err}</li>
                                                ))}
                                            </ul>
                                        </details>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Single-lesson regenerate */}
                        <div className="border border-ink/15 rounded-sm bg-bone p-6" data-testid="lessons-panel">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-2">
                                <BookOpen size={14}/> Lesson content
                            </div>
                            <div className="font-serif text-2xl text-ink">Regenerate a lesson</div>
                            <div className="text-sm text-ink/60 mt-1">
                                Currently {overview.lessons_generated} lesson{overview.lessons_generated === 1 ? "" : "s"} cached.
                                Regenerate refreshes the AI-written content for a course + topic.
                            </div>

                            <div className="mt-6 grid sm:grid-cols-3 gap-3 items-end">
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-ink/60">Course</label>
                                    <Select value={regenCourse} onValueChange={v => { setRegenCourse(v); setRegenTopic(""); }}>
                                        <SelectTrigger className="mt-2 rounded-sm" data-testid="admin-select-course"><SelectValue placeholder="Select course"/></SelectTrigger>
                                        <SelectContent>
                                            {courses.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-ink/60">Topic</label>
                                    <Select value={regenTopic} onValueChange={setRegenTopic} disabled={!selectedCourse}>
                                        <SelectTrigger className="mt-2 rounded-sm" data-testid="admin-select-topic"><SelectValue placeholder="Select topic"/></SelectTrigger>
                                        <SelectContent>
                                            {(selectedCourse?.topics || []).map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <Button onClick={regenerate} disabled={regenLoading || !regenTopic} className="bg-ink text-bone hover:bg-ink/90 rounded-sm h-10" data-testid="btn-regenerate-lesson">
                                    <ArrowsClockwise size={16} className="mr-2"/>
                                    {regenLoading ? "Regenerating…" : "Regenerate"}
                                </Button>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Resources tab */}
                    <TabsContent value="resources" className="mt-6 space-y-6">
                        {/* Bulk uploader — Cambridge filename auto-parser */}
                        <BulkUploader
                            onDone={async () => {
                                try {
                                    const { data } = await api.get("/admin/resources");
                                    setResources(data);
                                } catch { /* ignore */ }
                            }}
                        />

                        <div className="border border-ink/15 rounded-sm bg-bone p-6" data-testid="upload-panel">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-2">
                                <UploadSimple size={14} weight="duotone"/> Upload
                            </div>
                            <div className="font-serif text-2xl text-ink">Add a past / sample / topic-wise paper</div>
                            <div className="text-sm text-ink/60 mt-1">
                                PDF, DOC or DOCX. Files under 25 MB. Downloads are free &amp; public — no login required.
                            </div>

                            <form onSubmit={uploadResource} className="mt-6 grid md:grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-ink/60">Subject</label>
                                    <Select value={uploadForm.subject_code} onValueChange={(v) => setUploadForm(f => ({ ...f, subject_code: v }))}>
                                        <SelectTrigger className="mt-1 rounded-sm" data-testid="upload-subject"><SelectValue placeholder="Select subject"/></SelectTrigger>
                                        <SelectContent className="max-h-80">
                                            {subjects.map(s => (
                                                <SelectItem key={s.code} value={s.code}>
                                                    {s.board} · {s.code} — {s.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-ink/60">Type</label>
                                    <Select value={uploadForm.paper_type} onValueChange={(v) => setUploadForm(f => ({ ...f, paper_type: v }))}>
                                        <SelectTrigger className="mt-1 rounded-sm" data-testid="upload-type"><SelectValue/></SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="past_paper">Past paper</SelectItem>
                                            <SelectItem value="sample_paper">Sample paper</SelectItem>
                                            <SelectItem value="topic_wise">Topic-wise questions</SelectItem>
                                            <SelectItem value="notes">Important notes</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs uppercase tracking-widest text-ink/60">Title</label>
                                    <Input
                                        className="mt-1 rounded-sm"
                                        placeholder="e.g. Paper 12 — Pure Mathematics 1"
                                        value={uploadForm.title}
                                        onChange={(e) => setUploadForm(f => ({ ...f, title: e.target.value }))}
                                        data-testid="upload-title"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-ink/60">Year</label>
                                    <Input className="mt-1 rounded-sm" placeholder="2024" value={uploadForm.year} onChange={(e) => setUploadForm(f => ({ ...f, year: e.target.value }))} data-testid="upload-year"/>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-ink/60">Session</label>
                                    <Input className="mt-1 rounded-sm" placeholder="May/June" value={uploadForm.session} onChange={(e) => setUploadForm(f => ({ ...f, session: e.target.value }))} data-testid="upload-session"/>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-ink/60">Paper #</label>
                                    <Input className="mt-1 rounded-sm" placeholder="12" value={uploadForm.paper_number} onChange={(e) => setUploadForm(f => ({ ...f, paper_number: e.target.value }))} data-testid="upload-paper-num"/>
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-widest text-ink/60">File</label>
                                    <input
                                        type="file"
                                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                                        className="mt-1 block w-full text-sm text-ink file:mr-3 file:py-2 file:px-3 file:rounded-sm file:border-0 file:bg-ink file:text-bone hover:file:bg-ink/90"
                                        data-testid="upload-file"
                                    />
                                </div>
                                <div className="md:col-span-2">
                                    <label className="text-xs uppercase tracking-widest text-ink/60">Notes (optional)</label>
                                    <textarea
                                        rows={2}
                                        value={uploadForm.notes}
                                        onChange={(e) => setUploadForm(f => ({ ...f, notes: e.target.value }))}
                                        className="mt-1 w-full rounded-sm border border-ink/15 bg-bone px-3 py-2 text-sm text-ink"
                                        placeholder="Short blurb shown on the resource card"
                                        data-testid="upload-notes"
                                    />
                                </div>
                                <div className="md:col-span-2 flex items-center gap-4">
                                    <Button type="submit" disabled={uploading} className="bg-ink text-bone hover:bg-ink/90 rounded-sm h-11" data-testid="btn-upload-resource">
                                        <UploadSimple size={16} className="mr-2" weight="fill"/>
                                        {uploading ? "Uploading…" : "Upload paper"}
                                    </Button>
                                    {uploadFile && (
                                        <span className="text-xs text-ink/60 font-mono">{uploadFile.name} · {Math.round(uploadFile.size / 1024)} KB</span>
                                    )}
                                </div>
                            </form>
                        </div>

                        {/* Existing resources */}
                        <div className="border border-ink/15 rounded-sm bg-bone p-6" data-testid="resources-panel">
                            <div className="flex items-baseline justify-between mb-4">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.3em] text-gold">Library</div>
                                    <div className="font-serif text-2xl text-ink">{resources.length} paper{resources.length === 1 ? "" : "s"} live</div>
                                </div>
                                <a href="/resources" target="_blank" rel="noopener noreferrer" className="text-xs underline underline-offset-4 decoration-gold decoration-2 text-ink" data-testid="link-view-resources">
                                    View public page →
                                </a>
                            </div>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Subject</TableHead>
                                            <TableHead>Type</TableHead>
                                            <TableHead>Title</TableHead>
                                            <TableHead>Year / Session</TableHead>
                                            <TableHead className="text-right">Downloads</TableHead>
                                            <TableHead></TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {resources.length === 0 && (
                                            <TableRow><TableCell colSpan={6} className="text-center text-ink/50 py-8">No papers uploaded yet.</TableCell></TableRow>
                                        )}
                                        {resources.map(r => (
                                            <TableRow key={r.id} data-testid={`admin-resource-${r.id}`}>
                                                <TableCell className="font-mono text-xs">
                                                    <div>{r.subject_code}</div>
                                                    <div className="text-ink/50 text-[10px]">{r.subject_board}</div>
                                                </TableCell>
                                                <TableCell className="text-ink/70 text-xs uppercase tracking-wider">{r.paper_type.replace("_", " ")}</TableCell>
                                                <TableCell className="font-serif text-ink">{r.title}</TableCell>
                                                <TableCell className="text-ink/60 text-xs">
                                                    {[r.year, r.session, r.paper_number && `#${r.paper_number}`].filter(Boolean).join(" · ") || "—"}
                                                </TableCell>
                                                <TableCell className="text-right font-mono text-ink/70">{r.downloads || 0}</TableCell>
                                                <TableCell className="text-right">
                                                    <div className="flex justify-end gap-1">
                                                        <a href={`${api.defaults.baseURL}/resources/${r.id}/download`} target="_blank" rel="noopener noreferrer" title="Download" className="p-1.5 hover:bg-ink/10 rounded-sm">
                                                            <DownloadSimple size={14} className="text-ink"/>
                                                        </a>
                                                        <button onClick={() => deleteResource(r.id)} title="Delete" data-testid={`btn-delete-resource-${r.id}`} className="p-1.5 hover:bg-oxblood/10 rounded-sm">
                                                            <Trash size={14} className="text-oxblood"/>
                                                        </button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Enquiries tab */}
                    <TabsContent value="enquiries" className="mt-6">
                        <div className="border border-ink/15 rounded-sm bg-bone p-6" data-testid="enquiries-panel">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold mb-2">
                                <ChatCircleDots size={14} weight="duotone"/> Leads
                            </div>
                            <div className="font-serif text-2xl text-ink">
                                {enquiries.filter(e => e.status === "new").length} new · {enquiries.length} total
                            </div>
                            <div className="text-sm text-ink/60 mt-1">
                                Enquiries from Tuitions, Study Abroad and Resources pages. Mark as contacted after your call.
                            </div>

                            <div className="mt-6 overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Received</TableHead>
                                            <TableHead>Name</TableHead>
                                            <TableHead>Phone / Email</TableHead>
                                            <TableHead>Source</TableHead>
                                            <TableHead>Interest</TableHead>
                                            <TableHead>Status</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {enquiries.length === 0 && (
                                            <TableRow><TableCell colSpan={6} className="text-center text-ink/50 py-8">No enquiries yet.</TableCell></TableRow>
                                        )}
                                        {enquiries.map(e => (
                                            <TableRow key={e.id} data-testid={`enquiry-row-${e.id}`}>
                                                <TableCell className="text-xs text-ink/60 font-mono whitespace-nowrap">
                                                    {new Date(e.created_at).toLocaleString()}
                                                </TableCell>
                                                <TableCell className="font-serif text-ink">
                                                    {e.name}
                                                    {e.message && (
                                                        <div className="text-xs text-ink/60 font-sans mt-1 max-w-xs italic line-clamp-2">&ldquo;{e.message}&rdquo;</div>
                                                    )}
                                                </TableCell>
                                                <TableCell className="text-xs">
                                                    <a href={`tel:${e.phone}`} className="text-ink hover:text-gold-dark block">{e.phone}</a>
                                                    <a href={`mailto:${e.email}`} className="text-ink/60 hover:text-ink block">{e.email}</a>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-parchment text-ink/70">
                                                        {e.source}
                                                    </span>
                                                </TableCell>
                                                <TableCell className="text-sm text-ink/80 max-w-xs">
                                                    {e.interest || <span className="text-ink/40">—</span>}
                                                </TableCell>
                                                <TableCell>
                                                    <Select value={e.status} onValueChange={(v) => updateEnquiryStatus(e.id, v)}>
                                                        <SelectTrigger data-testid={`enquiry-status-${e.id}`} className={`rounded-sm h-8 text-xs w-32 ${
                                                            e.status === "new" ? "border-gold bg-gold/10" : e.status === "contacted" ? "border-emerald-600 bg-emerald-50" : "border-ink/20"
                                                        }`}>
                                                            <SelectValue/>
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="new">New</SelectItem>
                                                            <SelectItem value="contacted">Contacted</SelectItem>
                                                            <SelectItem value="closed">Closed</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </section>
            <Footer />
        </div>
    );
}

function Kpi({ icon, label, value, testid }) {
    return (
        <div className="border border-ink/15 rounded-sm p-5 bg-bone" data-testid={testid}>
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-ink/50">{icon} {label}</div>
            <div className="font-serif text-3xl text-ink mt-2">{value}</div>
        </div>
    );
}
